/** Pure `MotifThemeSerialized` derivations — the shared leaf for both the server writer (motif-state.ts) and
 * client code (hooks, selectors). No Admin SDK / node / i18n deps — App Router route handlers import this,
 * and next-i18next's React client APIs crash their RSC bundle. Entry-scoped: every helper reads ONE theme. */
import type { MotifThemeSerialized } from './serialize-theme';

/** A `generating` block older than this is an orphan (waitUntil is not durable) — surface it as failed. */
export const STALE_GENERATING_MS = 5 * 60 * 1000;

/** Max themes per wedding; creation is refused at the cap (no eviction). Lives on the pure leaf so client
 * UI (the gallery / new-theme cap check) can import it without pulling in the Admin-SDK writer. */
export const MAX_MOTIF_THEMES = 6;

/** Model-facing limit wording, returned by the server writer and the generation tools. Never translated —
 * the LLM reads it, not the couple. The couple-facing copy lives with its dialog (use-start-new-theme). */
export const THEME_LIMIT_MESSAGE = `You've reached the limit of ${MAX_MOTIF_THEMES} designs. Delete one to create another.`;

/** 'idle' = no such theme / no generation ever started. */
export type MotifEffectiveStatus = 'idle' | 'generating' | 'ready' | 'failed';

/** The theme's current draft hash = the last READY history entry (undefined when none). */
export const currentDraftCssHash = (theme: MotifThemeSerialized | undefined): string | undefined =>
  theme?.history?.length ? theme.history[theme.history.length - 1].cssHash : undefined;

/** Recency of an entry = its tail history `createdAt`, else `startedAt` (0 when neither). */
const themeRecency = (theme: MotifThemeSerialized): number =>
  theme.history?.length
    ? theme.history[theme.history.length - 1].createdAt
    : (theme.startedAt ?? 0);

/** The most-recently-updated entry (the one to activate on entry / after deleting the active one). */
export const mostRecentMotifTheme = (
  themes: MotifThemeSerialized[] | undefined,
): MotifThemeSerialized | undefined => {
  if (!themes?.length) return undefined;
  return themes.reduce((best, t) => (themeRecency(t) >= themeRecency(best) ? t : best));
};

/** Entries newest-first (tail `createdAt`, else `startedAt`) — the gallery order. Non-mutating. */
export const sortMotifThemesByRecency = (
  themes: MotifThemeSerialized[] | undefined,
): MotifThemeSerialized[] => [...(themes ?? [])].sort((a, b) => themeRecency(b) - themeRecency(a));

/** Effective status with the stale-generating guard applied (`now` injected for testability). */
export const effectiveMotifStatus = (
  theme: MotifThemeSerialized | undefined,
  now: number,
): MotifEffectiveStatus => {
  if (!theme || !theme.status) return 'idle';
  if (theme.status === 'generating') {
    return now - (theme.startedAt ?? 0) > STALE_GENERATING_MS ? 'failed' : 'generating';
  }
  return theme.status === 'failed' ? 'failed' : 'ready';
};
